import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Banner } from "@visioni/a3s-ui/components";
import { Text, View } from "@src/components";
import { parseTenantSignalFilters, useTenantSignals } from "@visioni/a3s-monitoring/app";
import { patriotToolsHref } from "../routes";
import { useActivationScopeOptions } from "../hooks/useActivationScopeOptions";
import { ActivationFilterBar, type ActivationFilterValues } from "../components/ActivationFilterBar";
import { SignalsTable } from "../components/SignalsTable";
import { QueryNotice } from "../components/QueryNotice";

/**
 * The tenant's signals, filtered the same way as its activations and by the
 * same URL params (`?site=&camera=&from=&to=`), so a filtered view can be moved
 * between the two lists by changing only the path.
 */
export function TenantSignalsScreen() {
  const router = useRouter();
  const { tenantId, site, camera, from, to } = useLocalSearchParams<{
    tenantId: string;
    site?: string;
    camera?: string;
    from?: string;
    to?: string;
  }>();
  const parsed = parseTenantSignalFilters({ site, camera, from, to });
  const filters = parsed.ok ? parsed.filters : {};

  const scope = useActivationScopeOptions();
  const list = useTenantSignals({ filters, enabled: parsed.ok });

  const onFiltersChange = (next: ActivationFilterValues) => {
    // Every key is always passed, so a cleared filter is removed from the URL
    // rather than left behind from the previous selection.
    router.setParams({ site: next.site, camera: next.camera, from: next.from, to: next.to });
  };

  return (
    <View style={styles.page}>
      <View style={styles.filterRow}>
        <ActivationFilterBar
          values={{
            ...(site && { site }),
            ...(camera && { camera }),
            ...(from && { from }),
            ...(to && { to }),
          }}
          onChange={onFiltersChange}
          sites={scope.sites}
          cameras={scope.cameras}
          isLoading={scope.isLoading}
        />
      </View>
      {!parsed.ok ?
        <View style={styles.notice}>
          <Banner variant="warning" title="Filters not applied" message={parsed.problem} />
        </View>
      : list.items === undefined || list.error !== null ?
        <View style={styles.notice}>
          <QueryNotice isLoading={list.isLoading} error={list.error} onRetry={list.refetch} />
        </View>
      : list.items.length === 0 && !list.pager.canGoBack ?
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted">
            No signals found
          </Text>
        </View>
      : <SignalsTable
          items={list.items}
          isStale={list.isPlaceholderData}
          isFetching={list.isFetching}
          activationHref={(ticketNo) =>
            patriotToolsHref(tenantId, `tenant-activations/${encodeURIComponent(ticketNo)}`)
          }
          siteLabel={scope.siteLabel}
          cameraLabel={scope.cameraLabel}
          pager={list.pager}
          nextCursor={list.nextCursor}
        />
      }
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  page: { flex: 1 },
  filterRow: {
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    // Above the list, so the filter dropdowns open over the rows.
    zIndex: 1,
  },
  notice: { paddingHorizontal: theme.spacing.xl },
  centered: { flex: 1, alignItems: "center", justifyContent: "center", padding: theme.spacing["4xl"] },
}));
